/**
 * CommissionStatementsPage
 *
 * View, approve, and export monthly referral commission statements.
 * Supports quick generation for "last month" or a custom period, and
 * surfaces payout-batch status so admins know what's been settled.
 */

import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { DatePicker } from '@/components/ui/date-picker'
import { useClinic } from '@/contexts/ClinicContext'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import {
  Loader2,
  RefreshCw,
  Eye,
  Check,
  X,
  Download,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import {
  listStatements,
  getStatement,
  generateStatements,
  submitStatement,
  approveStatement,
  voidStatement,
  exportStatement,
  CommissionStatement,
  centsToDollars,
  bpsToPercentage,
} from '@/services/commissionService'

const formatUSDate = (iso?: string | null) => {
  if (!iso) return ''
  try {
    return format(parseISO(iso), 'MM/dd/yyyy')
  } catch {
    return iso
  }
}

const STATUS_COLORS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  draft: 'secondary',
  pending_approval: 'outline',
  approved: 'default',
  exported: 'default',
  voided: 'destructive',
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  exported: 'Exported',
  voided: 'Voided',
}

const ROLE_LABELS: Record<string, string> = {
  practitioner: 'Practitioner',
  clinic_admin: 'Clinic Admin',
  admin: 'Admin',
  super_admin: 'System Admin',
}

export function CommissionStatementsPage() {
  const { clinic, isAdmin } = useClinic()
  const clinicId = clinic?.id
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [unpaidOnly, setUnpaidOnly] = useState(false)
  const [selectedStatement, setSelectedStatement] =
    useState<CommissionStatement | null>(null)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [showVoidDialog, setShowVoidDialog] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [generatePeriod, setGeneratePeriod] = useState<{
    start: Date | undefined
    end: Date | undefined
  }>({ start: undefined, end: undefined })
  const [pendingAction, setPendingAction] = useState<{
    type: 'submit' | 'approve' | 'export'
    statementId: string
  } | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  const { data: statementsData, isLoading } = useQuery({
    queryKey: [
      'commission-statements',
      clinicId,
      page,
      statusFilter,
      unpaidOnly,
    ],
    queryFn: () =>
      listStatements(clinicId!, {
        page,
        limit: 20,
        status: statusFilter || undefined,
        unpaidOnly: unpaidOnly || undefined,
      }),
    enabled: !!clinicId,
  })

  const { data: statementDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ['commission-statement', clinicId, selectedStatement?.id],
    queryFn: () => getStatement(clinicId!, selectedStatement!.id),
    enabled: !!clinicId && !!selectedStatement?.id,
  })

  const generateMutation = useMutation({
    mutationFn: () => {
      if (!generatePeriod.start || !generatePeriod.end) {
        return Promise.reject(new Error('Select a start and end date.'))
      }
      return generateStatements(
        clinicId!,
        format(generatePeriod.start, 'yyyy-MM-dd'),
        format(generatePeriod.end, 'yyyy-MM-dd'),
      )
    },
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['commission-statements'] })
      setShowGenerateDialog(false)
      const total = (result.generated || 0) + (result.skipped || 0)
      const parts = [`${result.generated || 0} generated`]
      if ((result.skipped || 0) > 0) parts.push(`${result.skipped} skipped`)
      if ((result.errors || 0) > 0) parts.push(`${result.errors} errors`)
      toast({
        title: 'Statements Generated',
        description: `Total: ${total} (${parts.join(', ')}).`,
      })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Generation failed',
        description: error.message,
      })
    },
  })

  const submitMutation = useMutation({
    mutationFn: (statementId: string) =>
      submitStatement(clinicId!, statementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-statements'] })
      queryClient.invalidateQueries({ queryKey: ['commission-statement'] })
      toast({ title: 'Submitted for approval' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      })
    },
  })

  const approveMutation = useMutation({
    mutationFn: (statementId: string) =>
      approveStatement(clinicId!, statementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-statements'] })
      queryClient.invalidateQueries({ queryKey: ['commission-statement'] })
      toast({ title: 'Statement approved' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      })
    },
  })

  const voidMutation = useMutation({
    mutationFn: ({
      statementId,
      reason,
    }: {
      statementId: string
      reason: string
    }) => voidStatement(clinicId!, statementId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-statements'] })
      queryClient.invalidateQueries({ queryKey: ['commission-statement'] })
      setShowVoidDialog(false)
      setVoidReason('')
      toast({ title: 'Statement voided' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      })
    },
  })

  const handleExport = async (statementId: string) => {
    try {
      const blob = await exportStatement(clinicId!, statementId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `commission-statement-${statementId}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      queryClient.invalidateQueries({ queryKey: ['commission-statements'] })
      toast({ title: 'CSV downloaded' })
    } catch {
      toast({
        variant: 'destructive',
        title: 'Export Failed',
        description: 'Could not export statement.',
      })
    }
  }

  const runPendingAction = async () => {
    if (!pendingAction) return
    setActionLoading(true)
    try {
      if (pendingAction.type === 'submit') {
        await submitMutation.mutateAsync(pendingAction.statementId)
      } else if (pendingAction.type === 'approve') {
        await approveMutation.mutateAsync(pendingAction.statementId)
      } else {
        await handleExport(pendingAction.statementId)
      }
      setPendingAction(null)
    } finally {
      setActionLoading(false)
    }
  }

  const confirmCopy = (() => {
    switch (pendingAction?.type) {
      case 'submit':
        return {
          title: 'Submit for Approval?',
          description:
            'This statement will be marked pending approval and can no longer be edited. Continue?',
        }
      case 'approve':
        return {
          title: 'Approve Statement?',
          description:
            'Approving locks this statement and makes it eligible for payout. Continue?',
        }
      case 'export':
        return {
          title: 'Export Statement?',
          description:
            'A CSV file will be downloaded with this statement\u2019s line items.',
        }
      default:
        return { title: '', description: '' }
    }
  })()

  const statements = statementsData?.data || []
  const pagination = statementsData?.pagination

  const payoutStatusLabel = useMemo(
    () => (s: CommissionStatement) => {
      if (!s.payout_batch_id) return 'Unpaid'
      return 'Batched'
    },
    [],
  )

  if (!clinicId) return <div className="p-6">No clinic selected.</div>
  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            You need clinic admin access to view referral statements.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Referral Statements</CardTitle>
            <CardDescription>
              Monthly commission statements per referrer. Generate a period,
              approve, then batch into a payout.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter || 'all'}
              onValueChange={v => {
                setStatusFilter(v === 'all' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="exported">Exported</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={unpaidOnly ? 'default' : 'outline'}
              onClick={() => {
                setUnpaidOnly(!unpaidOnly)
                setPage(1)
              }}
            >
              {unpaidOnly ? 'Unpaid Only ✓' : 'Unpaid Only'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setGeneratePeriod({ start: undefined, end: undefined })
                setShowGenerateDialog(true)
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referrer</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Referrals</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payout</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statements.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="text-center text-muted-foreground"
                      >
                        No statements. Click "Generate" to create statements
                        for a period.
                      </TableCell>
                    </TableRow>
                  )}
                  {statements.map(statement => {
                    const role = statement.role_snapshot || ''
                    const refName =
                      statement.referrer?.name ||
                      (statement.referrerUser
                        ? `${statement.referrerUser.first_name} ${statement.referrerUser.last_name}`
                        : statement.referrer_user_id?.slice(0, 8) || '—')
                    return (
                      <TableRow key={statement.id}>
                        <TableCell>{refName}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {ROLE_LABELS[role] ||
                              role.replace(/_/g, ' ') ||
                              '—'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatUSDate(statement.period_start)} – {formatUSDate(statement.period_end)}
                        </TableCell>
                        <TableCell>{statement.referral_count}</TableCell>
                        <TableCell>
                          $
                          {statement.revenueDollars ||
                            centsToDollars(statement.eligible_revenue_cents)}
                        </TableCell>
                        <TableCell className="font-medium">
                          $
                          {statement.commissionDollars ||
                            centsToDollars(statement.commission_amount_cents)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_COLORS[statement.status]}>
                            {STATUS_LABELS[statement.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              statement.payout_batch_id
                                ? 'default'
                                : 'secondary'
                            }
                          >
                            {payoutStatusLabel(statement)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <TooltipProvider delayDuration={200}>
                            <div className="flex items-center gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      setSelectedStatement(statement)
                                    }
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>View details</TooltipContent>
                              </Tooltip>
                              {statement.status === 'draft' && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        setPendingAction({
                                          type: 'submit',
                                          statementId: statement.id,
                                        })
                                      }
                                    >
                                      <Check className="h-4 w-4 text-green-500" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Submit for approval
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {statement.status === 'pending_approval' && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        setPendingAction({
                                          type: 'approve',
                                          statementId: statement.id,
                                        })
                                      }
                                    >
                                      <Check className="h-4 w-4 text-green-500" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Approve</TooltipContent>
                                </Tooltip>
                              )}
                              {statement.status === 'approved' && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        setPendingAction({
                                          type: 'export',
                                          statementId: statement.id,
                                        })
                                      }
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Export</TooltipContent>
                                </Tooltip>
                              )}
                              {!['voided', 'exported'].includes(
                                statement.status,
                              ) && !statement.payout_batch_id && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedStatement(statement)
                                        setShowVoidDialog(true)
                                      }}
                                    >
                                      <X className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Void</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages} (
                    {pagination.total} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= pagination.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Commission Statements</DialogTitle>
            <DialogDescription>
              Statements are generated for every referrer with captured guided
              orders in the period. Existing draft statements will be
              regenerated; approved / exported ones are skipped.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Start</Label>
                <DatePicker
                  value={generatePeriod.start}
                  onValueChange={date =>
                    setGeneratePeriod(p => ({ ...p, start: date }))
                  }
                  maxDate={generatePeriod.end}
                />
              </div>
              <div className="space-y-2">
                <Label>Period End</Label>
                <DatePicker
                  value={generatePeriod.end}
                  onValueChange={date =>
                    setGeneratePeriod(p => ({ ...p, end: date }))
                  }
                  minDate={generatePeriod.start}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowGenerateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={
                !generatePeriod.start ||
                !generatePeriod.end ||
                generateMutation.isPending
              }
            >
              {generateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Dialog */}
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Statement</DialogTitle>
            <DialogDescription>
              Voiding is permanent. Provide a reason for the audit log.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={voidReason}
                onChange={e => setVoidReason(e.target.value)}
                placeholder="Why is this being voided?"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowVoidDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (selectedStatement) {
                  voidMutation.mutate({
                    statementId: selectedStatement.id,
                    reason: voidReason,
                  })
                }
              }}
              disabled={!voidReason || voidMutation.isPending}
            >
              {voidMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Void
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Sheet */}
      <Sheet
        open={!!selectedStatement && !showVoidDialog}
        onOpenChange={open => !open && setSelectedStatement(null)}
      >
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto p-6">
          <SheetHeader className="flex-row items-start justify-between gap-4 space-y-0">
            <div className="space-y-2">
              <SheetTitle>Statement Details</SheetTitle>
              <SheetDescription>
                {formatUSDate(selectedStatement?.period_start)} – {formatUSDate(selectedStatement?.period_end)}
              </SheetDescription>
            </div>
            <SheetClose
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </SheetClose>
          </SheetHeader>

          {detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            statementDetails && (
              <div className="mt-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Referrer</p>
                    <p className="font-medium">
                      {statementDetails.referrer?.name ||
                        (statementDetails.referrerUser
                          ? `${statementDetails.referrerUser.first_name} ${statementDetails.referrerUser.last_name}`
                          : 'Unknown')}
                    </p>
                    {statementDetails.role_snapshot && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {ROLE_LABELS[statementDetails.role_snapshot] ||
                          statementDetails.role_snapshot.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant={STATUS_COLORS[statementDetails.status]}>
                      {STATUS_LABELS[statementDetails.status]}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Referrals
                    </p>
                    <p className="font-medium">
                      {statementDetails.referral_count}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Avg Commission Rate
                    </p>
                    <p className="font-medium">
                      {bpsToPercentage(statementDetails.commission_rate_bps)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Clinic Revenue
                    </p>
                    <p className="font-medium">
                      $
                      {statementDetails.revenueDollars ||
                        centsToDollars(
                          statementDetails.eligible_revenue_cents,
                        )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Commission Due
                    </p>
                    <p className="text-lg font-bold text-green-600">
                      $
                      {statementDetails.commissionDollars ||
                        centsToDollars(
                          statementDetails.commission_amount_cents,
                        )}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Line Items</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Patient</TableHead>
                        <TableHead>Revenue</TableHead>
                        <TableHead>Rate</TableHead>
                        <TableHead>Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {statementDetails.items?.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{formatUSDate(item.service_date) || 'N/A'}</TableCell>
                          <TableCell>
                            {item.patient
                              ? [
                                  item.patient.first_name,
                                  item.patient.middle_name,
                                  item.patient.last_name,
                                ]
                                  .filter(Boolean)
                                  .join(' ')
                              : 'Unknown'}
                          </TableCell>
                          <TableCell>
                            ${centsToDollars(item.clinic_revenue_cents)}
                          </TableCell>
                          <TableCell>
                            {bpsToPercentage(item.commission_rate_bps)}%
                          </TableCell>
                          <TableCell>
                            ${centsToDollars(item.commission_amount_cents)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  {statementDetails.status === 'draft' && (
                    <Button
                      onClick={() =>
                        setPendingAction({
                          type: 'submit',
                          statementId: statementDetails.id,
                        })
                      }
                    >
                      Submit for Approval
                    </Button>
                  )}
                  {statementDetails.status === 'pending_approval' && (
                    <Button
                      onClick={() =>
                        setPendingAction({
                          type: 'approve',
                          statementId: statementDetails.id,
                        })
                      }
                    >
                      Approve
                    </Button>
                  )}
                  {statementDetails.status === 'approved' && (
                    <Button
                      onClick={() =>
                        setPendingAction({
                          type: 'export',
                          statementId: statementDetails.id,
                        })
                      }
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  )}
                </div>
              </div>
            )
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!pendingAction}
        onOpenChange={open => {
          if (!open && !actionLoading) setPendingAction(null)
        }}
        title={confirmCopy.title}
        description={confirmCopy.description}
        onConfirm={runPendingAction}
        isLoading={actionLoading}
      />
    </div>
  )
}

export default CommissionStatementsPage
